import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { FaEdit } from "react-icons/fa";
import useAccess from "../../hooks/useAccess";
import "../../styles/Form.css";
import "../../styles/AdminGroupPage.css";
import DataTable from "../../components/DataTable";
import StyledSelect from "../../components/StyledSelect";

function AdminGroupPage() {
  const BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const navigate = useNavigate();

  const [groups, setGroups] = useState([]);
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");

  const { hasPermission } = useAccess();

  const PERMISSIONS = {
    groups: {
      create: hasPermission("GroupPage", "Group", "create"),
      edit: hasPermission("GroupPage", "Group", "edit"),
    },
  };

  useEffect(() => {
    axios
      .get(`${BASE_URL}/api/admin-groups`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      })
      .then((res) => setGroups(res.data))
      .catch((err) => console.error("Failed to load groups", err));

    axios
      .get(`${BASE_URL}/api/users`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      })
      .then((res) => setUsers(res.data))
      .catch((err) => console.error("Failed to load users", err));
  }, [BASE_URL]);

  const parseEmpGroups = (raw) => {
    if (!raw) return [];
    try {
      const parsed = typeof raw === "string" ? JSON.parse(raw.trim()) : raw;
      return Array.isArray(parsed) ? parsed.map(Number) : [];
    } catch {
      return [];
    }
  };

  const getUsersByGroupId = (groupId) =>
    users.filter((user) => parseEmpGroups(user.emp_groups).includes(Number(groupId)));

  const filteredGroups = useMemo(
    () =>
      groups.filter((group) => {
        const matchesSearch =
          group.pg_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (group.pg_email || "").toLowerCase().includes(searchTerm.toLowerCase());
        const matchesStatus =
          statusFilter === "All" ||
          (statusFilter === "Active" && group.pg_status === 1) ||
          (statusFilter === "Inactive" && group.pg_status === 0);
        return matchesSearch && matchesStatus;
      }),
    [groups, searchTerm, statusFilter]
  );

  const columns = [
    {
      key: "rowNumber",
      label: "#",
      width: "72px",
      getSortValue: (row) => row.pg_id,
      render: (_row, index) => index + 1,
    },
    {
      key: "pg_name",
      label: "Name",
      render: (row) => <span className="fw-bold">{row.pg_name}</span>,
    },
    {
      key: "members",
      label: "Users",
      getSortValue: (row) => getUsersByGroupId(row.pg_id).length,
      render: (row) => {
        const groupUsers = getUsersByGroupId(row.pg_id);
        return groupUsers.length > 0 ? (
          groupUsers.map((user) => user.full_name).join(", ")
        ) : (
          <span className="text-muted">No members</span>
        );
      },
    },
    {
      key: "pg_status",
      label: "Status",
      getSortValue: (row) => (row.pg_status === 1 ? "Active" : "Inactive"),
      render: (row) => (
        <span className={`status-badge ${row.pg_status === 1 ? "active" : "inactive"}`}>
          {row.pg_status === 1 ? "Active" : "Inactive"}
        </span>
      ),
    },
    {
      key: "actions",
      label: "Actions",
      sortable: false,
      width: "132px",
      headerClassName: "admin-action-column",
      cellClassName: "admin-action-column",
      render: (row) => (
        <div className="assignment-action-group">
          <button
            className="app-table-action-button is-edit"
            onClick={() => navigate(`/admin-groups/${row.pg_id}`)}
            disabled={!PERMISSIONS.groups.edit}
          >
            <FaEdit />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="form-wrapper my-3">
      <div className="dashboard-header d-flex justify-content-between align-items-center mb-4 pb-2 border-bottom">
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h3 className="fw-bold pb-2">Group Management</h3>
            {PERMISSIONS.groups.create && (
              <button
                className="btn btn-success dashboard-add-button"
                onClick={() => navigate("/admin-groups/create")}
              >
                Add Group
              </button>
            )}
          </div>

          <div className="d-flex gap-2 mb-3 admin-group-filters nw-filter-bar">
            <input
              type="text"
              className="form-control"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ maxWidth: "300px" }}
            />
            <StyledSelect
              value={statusFilter}
              onChange={setStatusFilter}
              options={[
                { value: "All", label: "All" },
                { value: "Active", label: "Active" },
                { value: "Inactive", label: "Inactive" },
              ]}
              minWidth={150}
              ariaLabel="Filter groups by status"
            />
          </div>

          <DataTable
            columns={columns}
            data={filteredGroups}
            rowKey="pg_id"
            itemsPerPage={15}
            tableClassName="admin-group-table"
            emptyMessage="No groups found."
          />
        </div>
      </div>
    </div>
  );
}

export default AdminGroupPage;
